import React from "react";
import {
    PlusCircleOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined,
    CheckCircleOutlined, InboxOutlined, AuditOutlined, SafetyCertificateOutlined,
    HistoryOutlined, TagsOutlined, DollarOutlined, BranchesOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

/**
 * Shared presentation data for the Production Logs pages.
 *
 * The backend stores each entry as "<Action>: <detail>" in production_log
 * (see BACKEND/api/production/production_log_helper.php). The action prefix is
 * what gives every entry its label, colour and icon here, so both the
 * Production Logs page and the batch activity history read the same mapping.
 */
export const ACTION_META = {
    Create: { label: "Batch Created", color: "blue", icon: <PlusCircleOutlined /> },
    Update: { label: "Batch Updated", color: "geekblue", icon: <EditOutlined /> },
    Delete: { label: "Batch Deleted", color: "red", icon: <DeleteOutlined /> },
    Start: { label: "Processing Started", color: "cyan", icon: <PlayCircleOutlined /> },
    Finish: { label: "Batch Finished", color: "green", icon: <CheckCircleOutlined /> },
    Input: { label: "Production Input Added", color: "purple", icon: <InboxOutlined /> },
    Approve: { label: "Input Approved", color: "gold", icon: <AuditOutlined /> },
    QC: { label: "Quality Control Recorded", color: "magenta", icon: <SafetyCertificateOutlined /> },
    Price: { label: "Selling Price Changed", color: "volcano", icon: <DollarOutlined /> },
    Label: { label: "Label Printed", color: "lime", icon: <TagsOutlined /> },
    AssignHarvest: { label: "Harvest Batch Assigned", color: "orange", icon: <BranchesOutlined /> },
    UnassignHarvest: { label: "Harvest Batch Unassigned", color: "red", icon: <DeleteOutlined /> },
    Other: { label: "Other Activity", color: "default", icon: <HistoryOutlined /> },
};

/** Ant Design tag colours mapped to hex, so timeline dots match the tags. */
export const ACTION_DOT_COLORS = {
    blue: "#1677ff",
    geekblue: "#2f54eb",
    red: "#ff4d4f",
    cyan: "#13c2c2",
    green: "#52c41a",
    purple: "#722ed1",
    gold: "#faad14",
    magenta: "#eb2f96",
    volcano: "#fa541c",
    lime: "#a0d911",
    orange: "#fa8c16",
    default: "#8c8c8c",
};

export const actionMeta = (action) => ACTION_META[action] || ACTION_META.Other;

export const actionDotColor = (action) =>
    ACTION_DOT_COLORS[actionMeta(action).color] || ACTION_DOT_COLORS.default;

/** "2026-09-21 14:32:10" (or "-" when unset / invalid). */
export const formatLogDateTime = (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-");

/** "just now" / "12 min ago" / "3 days ago" - no dayjs plugin needed. */
export const timeAgo = (value) => {
    const then = dayjs(value);
    if (!then.isValid()) return "";
    const minutes = dayjs().diff(then, "minute");
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    return `${Math.floor(months / 12)} year(s) ago`;
};

/** "SP-001 - Mango Pulp" for a log row (which carries sp_code / sp_name). */
export const logProductLabel = (record) => {
    if (!record) return "-";
    if (record.sp_code && record.sp_name) return `${record.sp_code} - ${record.sp_name}`;
    return record.sp_name || record.sp_code || "-";
};

const productionLogMeta = { ACTION_META, actionMeta, formatLogDateTime, timeAgo, logProductLabel };
export default productionLogMeta;
